import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import { fetchLiveOrders, setOrderStatus, type PosOrder } from '../../lib/api';

export default function LiveOrdersPage() {
  const [orders, setOrders] = useState<PosOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadOrders() {
      try {
        const response = await fetchLiveOrders();

        if (!isMounted) {
          return;
        }

        setOrders(response.orders);
        setError(null);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setError(loadError instanceof Error ? loadError.message : 'Unable to load live orders.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadOrders();
    const intervalId = window.setInterval(loadOrders, 15000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const handleStatusUpdate = async (orderId: number, newStatus: string) => {
    try {
      const response = await setOrderStatus(orderId, newStatus);
      setError(null);
      setOrders(currentOrders => {
        if (newStatus === 'completed') {
          return currentOrders.filter(order => order.id !== orderId);
        }

        return currentOrders.map(order => order.id === orderId ? response.order : order);
      });
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : 'Unable to update order status.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'preparing':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'completed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Live Orders</h1>
          <p className="text-sm text-gray-600">Track and manage current orders in real-time</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-4">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center text-sm text-gray-500">
            Loading live orders...
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <i className="ri-file-list-line text-6xl text-gray-300 w-20 h-20 flex items-center justify-center mx-auto mb-4"></i>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Active Orders</h2>
            <p className="text-gray-600">New orders will appear here</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {orders.map(order => (
              <div key={order.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{order.orderNumber}</h3>
                      <p className="text-sm text-teal-50">{formatTime(order.timestamp)}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="p-4">
                  <div className="mb-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <i className="ri-user-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                      <span className="text-sm font-medium text-gray-900">{order.customerName}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-phone-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                      <span className="text-sm text-gray-600">{order.customerPhone}</span>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-3 mb-4">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase">Order Items</h4>
                    <div className="space-y-2">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex justify-between items-center text-sm">
                          <span className="text-gray-700">
                            {item.quantity}x {item.name}
                          </span>
                          <span className="font-medium text-gray-900">
                            KSh {(item.price * item.quantity).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-3 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-gray-900">Total</span>
                      <span className="text-lg font-bold text-teal-600">
                        KSh {order.total.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    {order.status === 'preparing' && (
                      <button
                        onClick={() => handleStatusUpdate(order.id, 'completed')}
                        className="flex-1 bg-green-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Order Completed
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
